import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import useVerifyEmployee from '../hooks/useVerifyEmployee';
import useFingerprint from 'hooks/useFingerprint';

// Components
import Grid from '@material-ui/core/Grid';
import ContentBox from 'components/CncUI/ContentBox';
import Typography from 'components/CncUI/Typography';
import Radio from 'components/CncUI/Radio';
import Button from 'components/CncUI/Button';
import SupportFooter from 'components/SupportFooter';

// MUI
import LeftArrow from '@material-ui/icons/KeyboardArrowLeftRounded';

// Utilities
import getRecaptchaToken from 'utilities/getRecaptchaToken';
import Logger from 'utilities/logger';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  box: {
    margin: '40px auto',
    [breakpoints.up('md')]: {
      padding: '40px 140px',
      width: 376,
    },
    [breakpoints.down('sm')]: {
      width: 'auto',
      padding: '40px 20px',
    },
  },
  nextButton: {
    marginTop: 24,
  },
  backButton: {
    marginTop: 24,
    marginBottom: 24,
  },
  leftArrow: {
    borderRadius: '50%',
    backgroundColor: palette.cncUI.default.background,
    marginRight: 8,
  },
}));

const VerifyAddress = props => {
  const classes = useStyles();
  const logger = Logger('register.verifyAddress');
  const mutation = useVerifyEmployee();
  const { state, changeState, setStep, isMobile } = props;
  const { addresses, form, idType, fingerprint } = state;
  const [formData, setFormData] = useState(form);
  const [selectedAddress, setSelectedAddress] = useState(undefined);
  const [isVerifying, setIsVerifying] = useState(false);
  const handleSelect = e => {
    getFingerprint();
    setSelectedAddress(e.target.value);
    setFormData({ ...formData, address: e.target.value });
  };

  const onNext = () => {
    setStep('ThankYou');
  };

  const onError = () => {
    changeState({ error: new Error('Error VerifyAddress') });
    setStep('Error');
  };

  const getFingerprint = useFingerprint(
    fingerprint => changeState({ fingerprint }),
    error => logger.warn(`Fingerprint Error: ${error}`),
  );

  const submitForm = async () => {
    changeState({ form: formData });
    try {
      setIsVerifying(true);
      const recaptchaToken = await getRecaptchaToken('register');
      const submissionData = {
        ...formData,
        ssn: idType === 'sin' ? formData.sin : formData.ssn,
        countryFlag: idType === 'sin' ? 'CAN' : null,
        isMinor: state.isMinor,
        affidavit: state.affidavitDocumentId,
        idType,
        recaptchaToken,
        fingerprint,
      };

      setTimeout(() => {
        mutation(submissionData)
          .then(() => {
            onNext();
          })
          .catch(() => {
            onError();
          });
      }, 500);
    } catch (err) {
      onError();
    }
  };
  return (
    <>
      <ContentBox className={classes.box} id="VerifyAddress">
        <Grid container spacing={3} direction="column" alignItems="center">
          <Grid item xs={12} sm={8} md={12}>
            <Typography variant="h3">
              {state.isMinor
                ? 'Confirm minor identity'
                : 'Confirm your identity'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8} md={12}>
            <Typography variant="body2">
              Please select your current or previous address.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8} md={12}>
            <Radio.Group>
              <Grid container spacing={0}>
                {addresses.map((address, index) => (
                  <Grid item xs={12}>
                    <Radio
                      value={address}
                      labelPosition="right"
                      id={`VerifyAddressRadio${index}`}
                      onClick={handleSelect}
                    >
                      {address}
                    </Radio>
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Radio
                    labelPosition="right"
                    value="none"
                    onClick={handleSelect}
                  >
                    NONE OF THE ABOVE
                  </Radio>
                </Grid>
              </Grid>
            </Radio.Group>
          </Grid>
          <Grid item xs={12} sm={8} md={12} style={{ width: '100%' }}>
            <Button
              fullWidth
              id="VerifyAddressNextButton"
              className={classes.nextButton}
              disabled={!selectedAddress || isVerifying}
              onClick={() => submitForm()}
              loading={isVerifying}
            >
              Next
            </Button>
          </Grid>
          <Grid item xs={12} sm={8} md={12} style={{ width: '100%' }}>
            <Button
              tertiary
              fullWidth
              id="VerifyAddressBackButton"
              className={classes.backButton}
              onClick={() => setStep('FindAccount')}
            >
              <LeftArrow className={classes.leftArrow} />
              Back
            </Button>
          </Grid>
        </Grid>
      </ContentBox>

      <SupportFooter isMobile={isMobile} />
    </>
  );
};

export default VerifyAddress;
